import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const stages = [
  {
    num: '01',
    title: 'QUERY RECEIVED',
    subtitle: 'Customer asks AI',
    icon: 'ri-question-line',
    desc: 'A potential customer types a question into ChatGPT, Perplexity, or Google Gemini. They\'re not searching for keywords — they\'re asking for a recommendation in natural language.',
    detail: '"Who is the most trusted electrician in South Brisbane?"',
  },
  {
    num: '02',
    title: 'RETRIEVAL STAGE',
    subtitle: 'RAG — Sub-query search',
    icon: 'ri-search-eye-line',
    desc: 'The AI breaks the question into several sub-queries and retrieves the most relevant, credible sources it can find across the web. This is called Retrieval-Augmented Generation (RAG).',
    detail: 'Sources with strong authority signals, consistent mentions, and structured data win the retrieval stage.',
  },
  {
    num: '03',
    title: 'TRUST EVALUATION',
    subtitle: 'Source selection',
    icon: 'ri-shield-check-line',
    desc: 'The language model reads the retrieved sources and decides which ones to trust based on credibility signals: review consistency, directory citations, structured schema, and topical authority.',
    detail: 'GEO is about winning this stage — being selected as a trusted source, not just retrieved.',
  },
  {
    num: '04',
    title: 'ANSWER GENERATED',
    subtitle: 'Your business cited',
    icon: 'ri-checkbox-circle-line',
    desc: 'The AI synthesises its trusted sources into a confident, natural-language answer — citing and naming the businesses it found most authoritative, clear, and locally relevant.',
    detail: 'Your business name in that answer = zero ad spend, zero competition, direct recommendation.',
  },
];

export default function HowGeoWorksSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="how-it-works" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-ds-red/5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Under the Hood</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              HOW AI DECIDES
              <br />
              <span className="text-ds-red neon-text-red">WHO TO</span>
              <br />
              RECOMMEND.
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              When someone asks an AI a question, it doesn&apos;t just pass the query to a search engine. It uses a two-stage system called Retrieval-Augmented Generation (RAG). GEO is about winning both stages.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* RAG callout */}
        <div className="border border-ds-red/30 bg-ds-red/5 p-6 mb-16 flex flex-col md:flex-row items-center gap-6 reveal">
          <div className="w-12 h-12 flex items-center justify-center border border-ds-red flex-shrink-0">
            <i className="ri-cpu-line text-ds-red text-xl" />
          </div>
          <div>
            <span className="font-display text-ds-red text-xs tracking-widest uppercase">Retrieval-Augmented Generation (RAG)</span>
            <p className="font-body text-white/70 text-sm leading-relaxed mt-1">
              A two-stage AI process that first <strong className="text-white">searches the web for relevant sources</strong>, then uses a language model to synthesise those sources into a confident, natural-language answer — citing the ones it found most authoritative and accurate.
            </p>
          </div>
        </div>

        {/* 4-stage grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-ds-border/30 reveal">
          {stages.map((stage) => (
            <div key={stage.num} className="bg-ds-bg p-8 group hover:bg-ds-bg-2 transition-colors duration-300 relative overflow-hidden">
              {/* Stage number watermark */}
              <div className="absolute top-4 right-4 font-display text-6xl text-white/4 leading-none pointer-events-none">
                {stage.num}
              </div>
              <div className="w-10 h-10 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 mb-5">
                <i className={`${stage.icon} text-white/40 group-hover:text-ds-red text-lg transition-colors duration-300`} />
              </div>
              <div className="font-display text-ds-red text-xs tracking-widest mb-1">{stage.num}</div>
              <h3 className="font-display text-white text-lg leading-tight tracking-wide mb-1">{stage.title}</h3>
              <p className="font-body text-white/40 text-xs tracking-wider uppercase mb-4">{stage.subtitle}</p>
              <div className="h-px bg-ds-border mb-4" />
              <p className="font-body text-white/60 text-xs leading-relaxed mb-4">{stage.desc}</p>
              <div className="border border-ds-red/20 bg-ds-red/5 p-3">
                <p className="font-body text-white/50 text-xs leading-relaxed italic">{stage.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom summary strip */}
        <div className="mt-10 grid grid-cols-2 gap-px bg-ds-border/30 reveal">
          <div className="bg-ds-bg-2 p-8 flex items-center gap-6">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <i className="ri-search-2-line text-ds-red text-xl" />
            </div>
            <div>
              <div className="font-display text-white text-sm tracking-wider mb-1">STAGE 1 — Be Retrieved</div>
              <p className="font-body text-white/50 text-xs leading-relaxed">Your content must appear in the AI&apos;s initial retrieval sweep — built through authority signals, schema markup, and consistent directory citations.</p>
            </div>
          </div>
          <div className="bg-ds-bg-2 p-8 flex items-center gap-6">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-star-line text-ds-red text-xl" />
            </div>
            <div>
              <div className="font-display text-white text-sm tracking-wider mb-1">STAGE 2 — Be Selected</div>
              <p className="font-body text-white/50 text-xs leading-relaxed">Among retrieved sources, your content must win the trust evaluation — built through answer-ready content, topical depth, and credibility signals.</p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
